<script lang="ts">
	// The design system's Loading (design/components/core/Loading.jsx) in
	// Svelte: the compass mark, animated. The needle hunts while work is in
	// flight and settles on 34 degrees, the brand angle, when it lands, so the
	// loader at rest is the logo. The ring never moves.
	//
	// The motion is the design's own keyframes (bosla-seek, bosla-settle) from
	// tokens/motion.css, named here and defined there. data-seek on the needle
	// is what that file's reduced-motion rule swaps for a breathe; keep it.
	//
	// It belongs at panel scale. A button that is waiting says so in words.

	interface Props {
		/** What the wait is, for a screen reader. Comes from i18n. */
		label: string;
		/** Words beside the needle, when the wait needs saying out loud. */
		caption?: string;
		size?: 'xs' | 'sm' | 'md' | 'lg';
		/** ink on paper, dark or an ink card; on-speed on a red fill only. */
		tone?: 'ink' | 'on-speed';
		state?: 'seeking' | 'found';
		testId?: string;
	}

	let { label, caption, size = 'md', tone = 'ink', state = 'seeking', testId }: Props = $props();

	// xs is the floor: below it the compass stops reading as one.
	const sizes = { xs: 14, sm: 18, md: 26, lg: 38 };
	const tones = {
		ink: { ring: 'currentColor', north: 'var(--speed)', south: 'currentColor' },
		'on-speed': {
			ring: 'var(--on-speed)',
			north: 'var(--mark-on-speed)',
			south: 'var(--on-speed)',
		},
	};

	// One clip path per instance, or two loaders on a screen share an id.
	const clip = $props.id();
	const px = $derived(sizes[size]);
	const t = $derived(tones[tone]);
	const seeking = $derived(state === 'seeking');
</script>

<span
	class="loading {tone}"
	role="status"
	aria-label={label}
	aria-live="polite"
	data-testid={testId}
>
	<svg viewBox="0 0 26 26" width={px} height={px} aria-hidden="true">
		<!-- r=11.5, not 12, so the stroke's outer edge stays inside the box at
		     every scaled size. -->
		<circle cx="13" cy="13" r="11.5" fill="none" stroke={t.ring} stroke-width="2" />
		<g class="needle" class:seeking data-seek={seeking ? '' : undefined}>
			<clipPath id={clip}>
				<rect x="12" y="5.5" width="2" height="15" rx="1" />
			</clipPath>
			<g clip-path="url(#{clip})">
				<rect x="12" y="5.5" width="2" height="7.5" fill={t.north} />
				<rect x="12" y="13" width="2" height="7.5" fill={t.south} />
			</g>
		</g>
	</svg>
	{#if caption}
		<span class="caption">{caption}</span>
	{/if}
</span>

<style>
	.loading {
		display: inline-flex;
		align-items: center;
		gap: 10px;
	}

	svg {
		flex: none;
		display: block;
	}

	.needle {
		transform-origin: 13px 13px;
		transform: rotate(34deg);
		/* The landing. The hunt below replaces it while the work is in flight. */
		animation: bosla-settle var(--dur-settle) var(--ease-settle) both;
	}

	/* Linear, because bosla-seek carries its own easing per segment. */
	.needle.seeking {
		animation: bosla-seek var(--dur-seek) linear infinite;
	}

	.caption {
		font-family: var(--font-mono);
		font-size: 12.5px;
		letter-spacing: var(--tracking-label);
		color: var(--text-muted);
	}

	.on-speed .caption {
		color: var(--on-speed);
	}
</style>
